import { useContext, useEffect, useState } from 'react'
import * as React from 'react'
import { EditorState, Transaction } from 'prosemirror-state'
import { useSelector } from 'react-redux'
import { EditorProps } from 'prosemirror-view'
import { useHistory } from 'react-router-dom'
import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'

import * as Actions from 'redux/actions'
import RealtimeProvider from '../RealtimeProvider/RealtimeProvider'
import store from 'redux/store'
import { ArticleFlat, ElementPresence, RootState } from 'types'
import { DocPresenceContext } from 'stores/DocPresence'
import { ELEMENT_PRESENCE_WS_CHANNEL, getElementPresenceUpdateTx } from './utils'
import { consolidatedUserId } from 'common/PresenceAvatars/consolidatedUserId'
import { useLayerStates } from '../LayerStatesProvider/LayerStatesProvider'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { reloadPage } from 'utils/location'
import { checkFeatureFlagUserGroup, USERS } from 'utils/featureFlagUserGroups'
import { useIsBlaze } from 'hooks/useIsBlaze'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface CurrentPosition {
  layerId: string
  head?: number
  anchor?: number
  following?: number
}

interface Props {
  article: ArticleFlat
  readOnly?: boolean
  inCommentMode?: boolean
  children?: React.ReactNode
  editorProps?: EditorProps
}

const DEBOUNCE_INTERVAL = 500

const sendSocketEventWithDebounce = debounce(
  ({ head, anchor, following, layerId }: CurrentPosition, websocketSubscription: any) => {
    if (websocketSubscription) {
      websocketSubscription.perform('receive', {
        head,
        anchor,
        following,
        layer_id: layerId,
      })
    }
  },
  DEBOUNCE_INTERVAL,
)

export const StateWithPresenceProvider = ({
  article,
  inCommentMode,
  readOnly = false,
  children,
  editorProps,
}: Props) => {
  const { auth, anonymousUser } = useSelector(
    (state: RootState) => ({
      auth: state.auth,
      anonymousUser: state.anonymousUser,
      isRegistered: !!state.auth,
    }),
    isEqual,
  )
  const websocketConnection = useWebsocketConnection()
  const [websocketSubscription, setWebsocketSubscription] = useState<any>()
  const [currentPosition, setCurrentPosition] = useState<CurrentPosition>()
  const docPresenceState = useContext(DocPresenceContext)
  const history = useHistory()
  const { currentViewRef } = useProseInstance()
  const { currentLayer, clientUuid, updateLayerStates, getLayerState, layers } = useLayerStates()
  const isAlmanacUser = checkFeatureFlagUserGroup([USERS.withAlmanacEmail], auth?.email)
  const isBlaze = useIsBlaze()
  const { isMobile } = useResponsiveDesign()

  useEffect(() => {
    if (!websocketConnection || article.is_crdt) return

    const subscription = websocketConnection.subscriptions.create(
      { channel: ELEMENT_PRESENCE_WS_CHANNEL, article_id: article.id },
      {
        connected() {
          subscription?.perform('ask_to_broadcast')
        },
        received(payload: { element_presence: ElementPresence } | { broadcast: boolean }) {
          if ((payload as { broadcast: boolean }).broadcast) {
            // somebody just opened a doc and asked to broadcast the current element uuid
            setCurrentPosition((position) => {
              if (!position) return

              const { head, anchor, following, layerId: layer_id } = position

              subscription?.perform('receive', {
                head,
                anchor,
                following,
                layer_id,
              })
              return position
            })
          } else if (!readOnly) {
            const { element_presence, request_refresh } = payload as {
              element_presence?: ElementPresence
              request_refresh?: boolean
            }
            // Server requested to refresh page
            if (request_refresh) {
              reloadPage()
              return
            }
            // somebody broadcasted their current element uuid
            if (element_presence && element_presence.layer_id) {
              if (element_presence.following && element_presence.following === auth?.id) {
                docPresenceState?.upsertObserver(element_presence.user)
              } else {
                docPresenceState?.removeObserver(consolidatedUserId(element_presence.user))
              }

              const tx = getElementPresenceUpdateTx({
                articleId: article.id,
                state: currentViewRef.current?.state,
                auth,
                anonymousUser,
                elementPresence: element_presence,
                docPresenceState,
                layerId: element_presence.layer_id,
              })
              if (tx) {
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                currentViewRef.current!.dispatch(tx)
              }
            }
          }
        },
      },
    )
    setWebsocketSubscription(subscription)

    return () => {
      subscription?.unsubscribe()
      setCurrentPosition(undefined)
      setWebsocketSubscription(undefined)
      store.dispatch(Actions.setCommentPromptText(''))
    }
  }, [
    readOnly,
    auth,
    anonymousUser,
    article.id,
    article.is_crdt,
    websocketConnection,
    currentViewRef,
    docPresenceState,
    currentLayer?.id,
  ])

  useEffect(() => {
    if (!currentLayer?.id) {
      return
    }

    sendSocketEventWithDebounce(
      {
        head: currentViewRef.current?.state.selection.head,
        anchor: currentViewRef.current?.state.selection.anchor,
        following: consolidatedUserId(docPresenceState?.followedUser.user || null),
        layerId: currentLayer.id,
      },
      websocketSubscription,
    )
  }, [docPresenceState?.followedUser.user, websocketSubscription, currentViewRef, currentLayer?.id])

  const onTransaction = (tx: Transaction, newState: EditorState, layerId: string) => {
    if (readOnly) return
    if (!currentLayer) return
    if (tx.getMeta('snippetPreview')) return

    // save and broadcast the new element uuid on cursor change
    const { head, anchor } = newState.selection

    if (head && currentPosition?.head !== head) {
      const position = {
        layerId,
        head,
        anchor,
        following: consolidatedUserId(docPresenceState?.followedUser.user || null),
      }
      sendSocketEventWithDebounce(position, websocketSubscription)

      setCurrentPosition(position)
    }
  }

  if (!currentLayer) return null

  return (
    <RealtimeProvider
      getLayerState={getLayerState}
      currentLayer={currentLayer}
      articleId={article.id}
      crdt={article.is_crdt}
      isAlmanacUser={isAlmanacUser}
      auth={auth}
      clientUuid={clientUuid}
      history={history}
      inCommentMode={inCommentMode}
      editorProps={editorProps}
      onTransaction={onTransaction}
      readOnly={readOnly}
      websocketConnection={websocketConnection}
      updateLayerStates={updateLayerStates}
      layers={layers}
      isBlaze={isBlaze}
      isMobile={isMobile}
    >
      {children}
    </RealtimeProvider>
  )
}
